import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Camera, ImageUp, Trash2 } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { Button } from "@/components/ui";
import { formatBytes, prepareImage } from "@/lib/image";
import { catalogueRepository, type Product } from "@/repositories/catalogueRepository";

/**
 * The photograph on a product.
 *
 * Two ways in, and the order matters: **Take a photo** comes first because the
 * shop is standing next to the shelf, and a picture of the actual box on the
 * actual shelf is worth more than a studio shot of the same brand in packaging
 * that changed two years ago. `capture="environment"` opens the rear camera
 * directly on a phone and is ignored on a laptop, where the same control is an
 * ordinary file picker.
 *
 * When the shop has not photographed it, what shows is the master catalogue's
 * picture — and the wording says so, because "Remove" next to a picture the shop
 * never uploaded would be a lie about what the button does.
 */
export function ProductImageField({ product }: { product: Product }) {
  const client = useQueryClient();
  const camera = useRef<HTMLInputElement>(null);
  const picker = useRef<HTMLInputElement>(null);

  const [preview, setPreview] = useState<string | null>(null);
  const [saved, setSaved] = useState<{ from: number; to: number } | null>(null);

  // Revoke the object URL when it is replaced or the form closes. Without this
  // every photograph taken in a session stays in memory until the tab is shut.
  useEffect(() => () => void (preview && URL.revokeObjectURL(preview)), [preview]);

  const upload = useMutation({
    mutationFn: async (file: File) => {
      const prepared = await prepareImage(file);
      setSaved({ from: prepared.originalBytes, to: prepared.blob.size });
      setPreview((current) => {
        if (current) URL.revokeObjectURL(current);
        return URL.createObjectURL(prepared.blob);
      });
      return catalogueRepository.setImage(product.id, prepared.blob);
    },
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ["inventory"] });
      void client.invalidateQueries({ queryKey: ["sales"] });
    },
  });

  const remove = useMutation({
    mutationFn: () => catalogueRepository.clearImage(product.id),
    onSuccess: () => {
      setPreview(null);
      setSaved(null);
      void client.invalidateQueries({ queryKey: ["inventory"] });
      void client.invalidateQueries({ queryKey: ["sales"] });
    },
  });

  const shown = preview ?? product.image_url;
  const busy = upload.isPending || remove.isPending;

  function onPicked(event: React.ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    // Reset the input, or picking the same file twice in a row fires nothing the
    // second time and the shopkeeper concludes the button is broken.
    event.target.value = "";
    if (file) upload.mutate(file);
  }

  return (
    <section className="rounded-card border border-line p-4">
      <h3 className="text-sm font-semibold text-ink-900">Photograph</h3>

      <div className="mt-3 flex items-start gap-4">
        <div className="grid size-24 shrink-0 place-items-center overflow-hidden rounded-control border border-line bg-canvas">
          {shown ? (
            <img src={shown} alt="" className="size-full object-contain" />
          ) : (
            <span className="text-xs text-ink-500">No picture</span>
          )}
        </div>

        <div className="min-w-0 flex-1 space-y-2">
          <div className="flex flex-wrap gap-2">
            <Button size="sm" loading={busy} onClick={() => camera.current?.click()}>
              <Camera size={16} aria-hidden /> Take a photo
            </Button>
            <Button
              variant="ghost"
              size="sm"
              loading={busy}
              onClick={() => picker.current?.click()}
            >
              <ImageUp size={16} aria-hidden /> Choose a file
            </Button>
            {product.has_own_image && (
              <Button
                variant="ghost"
                size="sm"
                loading={remove.isPending}
                onClick={() => remove.mutate()}
              >
                <Trash2 size={16} aria-hidden /> Remove
              </Button>
            )}
          </div>

          <p className="text-xs text-ink-500">
            {product.has_own_image
              ? "This shop's own photograph."
              : shown
                ? "From the shared catalogue. Take your own and it will be used instead."
                : "A picture makes a product findable at a glance on the till."}
          </p>

          {saved && saved.to < saved.from && (
            <p className="text-xs text-ink-500">
              Shrunk from {formatBytes(saved.from)} to {formatBytes(saved.to)} before uploading.
            </p>
          )}

          {upload.error != null && (
            <p className="text-xs text-danger-700">
              {upload.error instanceof Error ? upload.error.message : "That picture was refused."}
            </p>
          )}
        </div>
      </div>

      <input
        ref={camera}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={onPicked}
      />
      <input ref={picker} type="file" accept="image/*" className="hidden" onChange={onPicked} />
    </section>
  );
}
